import { AUTHOR_ASSETS_FOLDER, LEARNER_ASSETS_FOLDER } from '../constants';
import { AssetMetadata, AssetOwnerCategory, AssetType } from '../models/Asset';

import AuthService from './AuthService';
import Config from '../config';
import HttpClient from './HttpClient';
import { documentFiles } from './MimeTypes';

class AssetService {
  private sas: string;

  async getContainerSas() {
    const containerName = AuthService.Tenant;
    const token = AuthService.getAccessToken();
    await HttpClient.get<{ sas: string }>(
      `${Config.ASSET_DS}/api/sas?container=${containerName}`,
      {
        authorization: `Bearer ${token}`
      }
    )
      .then((value) => (this.sas = value.data.sas))
      .catch((error) => (this.sas = null));
  }

  /**
   * Obtain a SAS for the container using a module or course sharing key.
   * @param linkkey Sharing link key.
   */
  async getConatinerSasFromLink(linkkey: string): Promise<void> {
    const containerName = AuthService.Tenant;
    try {
      const { data } = await HttpClient.get<{ sas: string }>(
        `${Config.ASSET_DS}/api/public/sas/link/${linkkey}?container=${containerName}`
      );
      this.sas = data.sas;
    } catch (err) {
      this.sas = null;
    }
  }

  public getAssetFolder(category: AssetOwnerCategory, owner: number) {
    return category === 'author'
      ? `${AUTHOR_ASSETS_FOLDER}/`
      : `${LEARNER_ASSETS_FOLDER}/${owner}/`;
  }

  public getBlobUrl(location: string) {
    return location
      ? `https://${Config.STORAGE_ACCOUNT}.blob.core.windows.net/${AuthService.Tenant}/${location}?${this.sas}`
      : null;
  }

  public getAssetUrl(assetkey: string) {
    return assetkey ? this.getBlobUrl(`author_assets/${assetkey}`) : null;
  }

  public async getLearnerAssetUrl(userkey: number, assetkey: string) {
    const blobSas = await this.getBlobSas(
      `${this.getAssetFolder('learner', userkey)}${assetkey}`
    );
    return assetkey
      ? `https://${Config.STORAGE_ACCOUNT}.blob.core.windows.net/${AuthService.Tenant}/learner_assets/${userkey}/${assetkey}?${blobSas}`
      : null;
  }

  private async getBlobSas(assetkey: string) {
    try {
      const containerName = AuthService.Tenant;
      const token = AuthService.getAccessToken();
      const { data } = await HttpClient.get<{ sas: string }>(
        `${Config.ASSET_DS}/api/sas?container=${containerName}&assetkey=${assetkey}`,
        {
          authorization: `Bearer ${token}`
        }
      );

      return data.sas;
    } catch (_) {
      return null;
    }
  }

  public async getAssetMetadataAsync(
    container: string,
    category: AssetOwnerCategory,
    owner: number,
    assetkey: string
  ) {
    try {
      const folder = this.getAssetFolder(category, owner);
      const token = AuthService.getAccessToken();
      const { data } = await HttpClient.get<AssetMetadata>(
        `${Config.ASSET_DS}/api/containers/${container}/categories/${category}/owners/${owner}/assets/${assetkey}`,
        {
          authorization: `Bearer ${token}`
        }
      );

      return data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * The video encoder generates a thumbnail image.
   * The thumbnail assetkey is the video assetkey with '_Thumb.jpg'.
   * @param {string} assetkey Video's assetkey
   * @returns {string} Video thumbnail's assetkey
   */
  public getVideoThumbnailAssetkey(assetkey: string): string {
    return `${assetkey}_Thumb.jpg`;
  }

  public getAssetTypeFromMime(mimeType: string): AssetType {
    let assetType: AssetType = null;
    if (mimeType?.startsWith('image')) {
      assetType = 'image';
    }
    if (mimeType?.startsWith('video')) {
      assetType = 'video';
    }
    if (mimeType?.startsWith('audio')) {
      assetType = 'audio';
    }
    if (mimeType?.startsWith('application') || mimeType?.startsWith('text')) {
      assetType = 'document';
    }
    return assetType;
  }

  public getAcceptFileTypes(types: AssetType[]): string {
    const acceptTypes: string[] = [];

    if (types.includes('image')) {
      acceptTypes.push('image/*');
    }
    if (types.includes('video')) {
      acceptTypes.push('video/*');
    }
    if (types.includes('audio')) {
      acceptTypes.push('audio/*');
    }
    if (types.includes('pdf')) {
      acceptTypes.push('application/pdf');
    }
    if (types.includes('document')) {
      acceptTypes.push(...documentFiles);
    }

    return acceptTypes.join(',');
  }
}

export default new AssetService();
